import * as React from 'react';

import { Image } from '@/hen-ui/atomics';
import { LOGO_BOX, LOGO_BOX_SM } from '@/hen-ui/constants/logoBoxDimensions';
import { cn } from '@/hen-ui/utils';

import { logoBoxVariants, type LogoBoxVariantProps } from './logo-box.variants';

export interface LogoBoxProps
  extends React.HTMLAttributes<HTMLDivElement>,
    LogoBoxVariantProps {
  /** Logo image URL */
  src: string;
  /** Alt text for the logo */
  alt: string;
}

/**
 * LogoBox — composite wrapper for logos that need HEN logo-box dimensions and styling.
 * Composes the Image atomic with object-fit scale-down so a logo is never
 * cropped or enlarged past its intrinsic size.
 */
export const LogoBox = React.forwardRef<HTMLDivElement, LogoBoxProps>(
  ({ size = 'default', shadow, src, alt, className, ...props }, ref) => {
    const isFill = size === 'fill';
    const dims = size === 'sm' ? LOGO_BOX_SM : LOGO_BOX;

    return (
      <div
        ref={ref}
        className={cn(logoBoxVariants({ size, shadow }), className)}
        {...props}
      >
        <Image
          src={src}
          alt={alt}
          objectFit='scale-down'
          {...(isFill
            ? { className: 'min-h-0 h-full w-full' }
            : {
                width: dims.width,
                height: dims.height,
                // Fill the fixed-dimension box and clamp to it so a tall logo
                // can't grow past the container height (Tailwind Preflight
                // forces `img { height: auto }`, which otherwise overrides the
                // `height` attribute and lets the image overflow + get cropped
                // by the box's `overflow-hidden`). `object-scale-down` then
                // fits the logo inside the box and does not enlarge it.
                className: 'h-full w-full max-h-full max-w-full',
              })}
        />
      </div>
    );
  },
);

LogoBox.displayName = 'LogoBox';
